import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2 } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { Bubble, GlassBtn, PatientList, DoctorList } from "./FlowPieces";
import { PatientDraft, RxDraft, ReferralDraft, ApptDraft, CertDraft } from "./DraftCards";

const PICK = "¿Para qué paciente? Escribe su nombre abajo o elígelo de la lista.";
const FLOWS = {
  patient: {
    steps: ["input"],
    ask: () => "¿Cuál es el nombre del paciente? Escribe sus datos (nombre, edad, cédula, teléfono, alergias…) o sube una foto de su cédula o documento y yo lleno la ficha.",
    hint: "Ej: Juan Pérez, 34 años, cédula 001…",
  },
  prescription: {
    steps: ["pick", "input"],
    ask: (p) => `¿Qué le vas a recetar a ${p.full_name}? Ej: amoxicilina 500 mg cada 8 horas por 7 días.`,
    hint: "Escribe o dicta la receta…",
  },
  referral: {
    steps: ["pick", "doctor", "input"],
    ask: (p, d) => `¿Cuál es el motivo del referido de ${p.full_name} a ${d.name}? Lo redacto por ti.`,
    hint: "Motivo del referido…",
  },
  certificate: {
    steps: ["pick", "input"],
    ask: (p) => `¿Qué debe decir el certificado de ${p.full_name}? Ej: faringitis aguda, reposo 3 días, para el trabajo.`,
    hint: "Diagnóstico, reposo y para qué…",
  },
  appointment: {
    steps: ["pick", "input"],
    ask: (p) => `¿Cuándo y para qué es la cita de ${p.full_name}? Ej: mañana a las 3 pm, control.`,
    hint: "Ej: viernes 10am, seguimiento…",
  },
};

const localNow = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const day = d.toLocaleDateString("es-ES", { weekday: "long" });
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())} (${day})`;
};

const toB64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export const AiFlow = forwardRef(({ type, initialPatient, onHint, onExit, onRestart }, ref) => {
  const cfg = FLOWS[type];
  const navigate = useNavigate();
  const [patient, setPatient] = useState(initialPatient || null);
  const [doctor, setDoctor] = useState(null);
  const [step, setStep] = useState(initialPatient ? cfg.steps.find((s) => s !== "pick") : cfg.steps[0]);
  const [log, setLog] = useState([]);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState(null);
  const [lastText, setLastText] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const say = (role, text) => setLog((l) => [...l, { role, text }]);

  const saidRef = useRef(null);
  useEffect(() => {
    if (saidRef.current === step) return;
    saidRef.current = step;
    if (step === "pick") say("assistant", PICK);
    if (step === "doctor") say("assistant", "¿A qué doctor lo refieres? Estos son los doctores de tu chat.");
    if (step === "input") say("assistant", cfg.ask(patient, doctor));
    onHint(step === "pick" ? "Buscar paciente…" : step === "doctor" ? "Buscar doctor…" : step === "input" ? cfg.hint : step === "done" ? "Pregunta lo que sea…" : "Escribe un ajuste o confirma…");
    setQuery("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [log, step, draft, result]);

  const next = () => setStep(cfg.steps[cfg.steps.indexOf(step) + 1]);

  const pickPatient = (p) => {
    say("user", p.full_name);
    setPatient(p);
    setStep(cfg.steps[cfg.steps.indexOf("pick") + 1]);
  };
  const pickDoctor = (d) => {
    say("user", `${d.name}${d.specialty ? ` · ${d.specialty}` : ""}`);
    setDoctor(d);
    next();
  };

  const runDraft = async (payload) => {
    setStep("loading");
    try {
      const { data } = await api.post("/ai/draft", {
        kind: type,
        patient_id: patient?.id,
        now: localNow(),
        to_doctor: doctor ? { name: doctor.name, specialty: doctor.specialty } : undefined,
        ...payload,
      });
      if (type === "referral" && !data.urgency) data.urgency = "rutina";
      setDraft(data);
      say("assistant", "Listo. Revisa el borrador, ajusta lo que necesites y confirma.");
      setStep("review");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "La IA no pudo armar el borrador");
      setStep(draft ? "review" : "input");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 6_000_000) return toast.error("La imagen es muy grande (máx 6MB)");
    say("user", `Documento: ${f.name}`);
    runDraft({ image_base64: await toB64(f) });
  };

  useImperativeHandle(ref, () => ({
    text: (t) => {
      if (step === "pick" || step === "doctor") return setQuery(t);
      if (step === "input") {
        say("user", t);
        setLastText(t);
        return runDraft({ text: t });
      }
      if (step === "review") {
        say("user", t);
        const combined = `${lastText}\nAjuste del doctor: ${t}`;
        setLastText(combined);
        return runDraft({ text: `${combined}\nBorrador actual (respeta los cambios manuales): ${JSON.stringify(draft)}` });
      }
      if (step === "done") onExit(t);
    },
    attach: () => (type === "patient" && ["input", "review"].includes(step) ? fileRef.current?.click() : null),
  }));

  const save = async () => {
    setSaving(true);
    try {
      let data;
      if (type === "patient") {
        if (!draft.full_name?.trim()) throw new Error("Falta el nombre del paciente");
        ({ data } = await api.post("/patients", draft));
      } else if (type === "prescription") {
        const items = (draft.items || []).filter((i) => i.medication?.trim()).map((i) => ({
          medication: i.medication, dosage: i.dosage || "", frequency: i.frequency || "", duration: i.duration || "",
          quantity: i.quantity || null, instructions: i.instructions || null,
        }));
        if (!items.length) throw new Error("Agrega al menos un medicamento");
        ({ data } = await api.post("/prescriptions", { patient_id: patient.id, diagnosis: draft.diagnosis, notes: draft.notes, items }));
      } else if (type === "referral") {
        ({ data } = await api.post("/referrals", {
          patient_id: patient.id, to_doctor_id: doctor.id, reason: draft.reason, clinical_notes: draft.clinical_notes || "", urgency: draft.urgency,
        }));
      } else if (type === "certificate") {
        if (!draft.diagnosis?.trim()) throw new Error("Falta el diagnóstico");
        ({ data } = await api.post("/certificates", {
          patient_id: patient.id, diagnosis: draft.diagnosis, note: draft.note || "", reason: draft.reason || "a petición del interesado",
        }));
      } else {
        const when = new Date(draft.date);
        if (isNaN(when)) throw new Error("Fecha inválida");
        ({ data } = await api.post("/appointments", {
          patient_id: patient.id, office_id: null, date: when.toISOString(),
          duration_min: parseInt(draft.duration_min) || 30, reason: draft.reason, status: "scheduled",
        }));
      }
      setResult(data);
      setStep("done");
    } catch (e) {
      toast.error(e.response ? formatErr(e.response.data?.detail) : e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    say("assistant", "Cancelado. No guardé nada.");
    setDraft(null);
    setStep("done");
  };

  const cardProps = { draft, set: setDraft, onConfirm: save, onCancel: cancel, saving };
  const DONE = {
    patient: () => ({ text: `Paciente ${result.full_name} creado.`, actions: [["Ver ficha", () => navigate(`/doctor/pacientes/${result.id}`)], ["Agendar cita", () => onRestart("appointment", result)]] }),
    prescription: () => ({ text: `Receta creada para ${patient.full_name}.`, actions: [["Imprimir receta", () => window.open(`/recetas/${result.id}/imprimir`, "_blank", "noopener")]] }),
    referral: () => ({ text: `Referido enviado a ${doctor.name}.`, actions: [["Ver referido", () => window.open(`/referidos/${result.id}/imprimir`, "_blank", "noopener")]] }),
    certificate: () => ({
      text: `Certificado creado para ${patient.full_name}.`,
      actions: [["Ver certificado", () => navigate(`/doctor/certificados/${result.id}`)]],
    }),
    appointment: () => ({
      text: `Cita agendada para ${patient.full_name}: ${new Date(result.date).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })}.`,
      actions: [["Ver agenda", () => navigate("/doctor/agenda")]],
    }),
  };
  const done = result ? DONE[type]() : null;

  const TITLES = { patient: "Nuevo paciente", prescription: "Nueva receta", referral: "Nuevo referido", appointment: "Nueva cita", certificate: "Nuevo certificado" };
  return (
    <div className="space-y-2.5 pt-1" data-testid={`cai-flow-${type}`}>
      {step !== "done" && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/80">{TITLES[type]}</span>
          <button type="button" onClick={() => onExit()} data-testid="cai-flow-exit" className="h-7 px-3 rounded-full bg-[#2B4A6F]/25 border border-white/35 text-[11.5px] font-semibold text-white hover:bg-[#2B4A6F]/40 transition-colors">
            Cancelar
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="cai-flow-file" />
      {log.map((m, i) => (
        <Bubble key={i} role={m.role} testid={`cai-flow-msg-${i}`}>{m.text}</Bubble>
      ))}
      {step === "pick" && <PatientList query={query} onPick={pickPatient} />}
      {step === "doctor" && <DoctorList query={query} onPick={pickDoctor} />}
      {step === "input" && type === "patient" && (
        <GlassBtn onClick={() => fileRef.current?.click()} data-testid="cai-flow-upload">
          <span className="inline-flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Subir documento</span>
        </GlassBtn>
      )}
      {step === "loading" && (
        <div className="flex items-center gap-2 text-white/85 text-[12px]" data-testid="cai-flow-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> La IA está preparando el borrador…
        </div>
      )}
      {step === "review" && draft && (
        <>
          {type === "patient" && <PatientDraft {...cardProps} />}
          {type === "prescription" && <RxDraft {...cardProps} />}
          {type === "referral" && <ReferralDraft {...cardProps} doctor={doctor} />}
          {type === "appointment" && <ApptDraft {...cardProps} />}
          {type === "certificate" && <CertDraft {...cardProps} />}
        </>
      )}
      {done && (
        <div className="cai-glass rounded-[22px] p-4 cai-fade-in" data-testid="cai-flow-done">
          <div className="flex items-start gap-2 text-[13px] text-white">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#B8FFD9]" /> {done.text}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {done.actions.map(([label, fn]) => (
              <GlassBtn key={label} primary onClick={fn} data-testid={`cai-flow-done-${label.split(" ")[0].toLowerCase()}`}>{label}</GlassBtn>
            ))}
            <GlassBtn onClick={() => onExit()} data-testid="cai-flow-finish">Terminar</GlassBtn>
          </div>
        </div>
      )}
      {step === "done" && !done && (
        <GlassBtn onClick={() => onExit()} data-testid="cai-flow-finish">Volver</GlassBtn>
      )}
      <div ref={endRef} />
    </div>
  );
});
